import {Injectable} from '@angular/core';
import {NavigationExtras, Router} from '@angular/router';
import {TranslateService} from '@ngx-translate/core';
import {LocalizeRouterService} from 'localize-router';
import {constants} from 'src/app/config/constants';
import {DateDiffModel} from 'src/app/models/inputs/date-diff-model';
import {DateModel} from 'src/app/models/inputs/date-model';
import {YearType} from 'src/app/models/type/year.type.enum';
import {DateDifferenceService} from 'src/app/shared/display/date-difference.service';
import * as moment from 'moment';

@Injectable()
export class UtilityService {

  constructor(
    private router: Router,
    private localize: LocalizeRouterService,
    private translate: TranslateService,
    private dateDifferenceService: DateDifferenceService
  ) {
  }


  /**
   * router navigate with translate route
   * e.g. /login -> /th/login
   */
  routerNavigate(url: string | any[], queryParams?, extras?: NavigationExtras) {
    const translatedPath: any = this.localize.translateRoute(url);
    if (url instanceof Array) {
      this.router.navigate(translatedPath, extras ? extras : {queryParams});
    } else {
      this.router.navigate([translatedPath], extras ? extras : {queryParams});
    }
  }

  routerNavigateQq(url: string | any[], queryParams?, extras?: NavigationExtras) {
    const translatedPath: any = this.localize.translateRoute(url);
    if (url instanceof Array) {
      this.router.navigate(translatedPath, extras ? extras : {queryParams : { params: queryParams }});
    } else {
      this.router.navigate([translatedPath], extras ? extras : {queryParams : { params: queryParams }});
    }
  }



  /**
   * Convert date string or date to date model,
   * Default Year: input(AD) output(current language: th:BE, en:AD)
   * example input: '2019-05-18T10:06:03.820+0000'
   * example input: new Date()
   * todo writing code return year type
   */
  convertDateToModel(value: any, typeIn?: YearType, typeOut?: YearType): DateModel {
    if (!value) {
      return null;
    } else if (this.isObjectDateModel(value)) {
      const val: any = value;
      return new DateModel(val.day, val.month, val.year);
    }
    const dateModel: DateModel = new DateModel();
    let date: Date;
    if (!typeOut) {
      typeOut = this.translate.currentLang === 'th' ? YearType.Buddhist : YearType.AnnoDomini;
    }

    if (typeof value === 'number') {
      date = new Date(value);
    } else if (typeof value === 'string' ) {
      date = new Date( value.substr(0, 10));
    } else if (value instanceof Date) {
      date = value;
    }

    if (date instanceof Date) {
      try {
        dateModel.year = date.getFullYear() as number + (typeOut === YearType.Buddhist ? 543 : 0);
        dateModel.month = date.getMonth() as number + 1 ;
        dateModel.day = date.getDate() as number ;
      } catch (e) {

      }
    }
    return dateModel;
  }

  /**
   * Convert date string or date model to date,
   * Default Year: input(AD) output(BE)
   * example input: '2019-05-18T10:06:03.820+0000'
   * example input: {year: 1996, month:12, day: 22}
   * todo writing code return year type
   */
  convertDateToDate(value: string | DateModel, typeIn?: YearType, typeOut: YearType = YearType.AnnoDomini): Date {
    if (!value) {
      return null;
    }

    let date: Date = null;
    if (!typeIn) {
      typeIn = this.translate.currentLang === 'th' ? YearType.Buddhist : YearType.AnnoDomini;
    }

    if ((value instanceof DateModel || typeof value === 'object') && value.day && value.month && value.year) {
      // date = new Date();
      date = new Date(value.year as number, value.month as number, value.day as number)
      date.setFullYear(value.year as number - (typeIn === YearType.Buddhist ? 543 : 0));
      date.setMonth(value.month as number - 1);
      date.setDate(value.day as number);
    } else if ( typeof value === 'number') {
      try {
        date = new Date(value);
      } catch (e) {
        date = null;
      }
    } else if (typeof value === 'string') {
      try {
        date = new Date(value.substr(0, 10));
      } catch (e) {
        date = null;
      }
    }
    return date;
  }

  convertDateFormatToDate(value, format = 'DD/MM/YYYY'): Date {
    if (value == null || value === '' || typeof value === 'undefined') {
      return null;
    }
    const lang = this.translate.currentLang ? this.translate.currentLang : constants.language;
    const val = moment(value, format, lang, true);
    if (val.isValid()) {
      if (lang === 'th') {
        val.subtract(543, 'year');
      }
      return new Date(val.format());
    }
    return null;
  }

  dateDiffCurrentDate(date: any, type: 'date' | 'obj' = 'obj', isQuickQuotek?: boolean): DateDiffModel {
    return this.dateDifferenceService.diffCurrentDate(date, type, isQuickQuotek);
  }

  calYearsOfAge(data: 'string' | Date | DateModel): number | null {
    let dateDiff: DateDiffModel;
    if (data && data instanceof Date) {
      dateDiff = this.dateDifferenceService.diffCurrentDate(data, 'date');
    } else if (this.isObjectDateModel(data)) {
      dateDiff = this.dateDifferenceService.diffCurrentDate(data, 'obj');
    } else if (data && typeof data === 'string') {
      try {
        dateDiff = this.dateDifferenceService.diffCurrentDate(new Date(data), 'date');
      } catch (e) {
        dateDiff = null;
      }
    }
    return dateDiff ? dateDiff.years : null;
  }

  isNotEmptyDateModel(date: any): boolean {
    if (date) {
      return this.isNotEmpty(date.year) && this.isNotEmpty(date.month) && this.isNotEmpty(date.day);
    }
    return false;
  }

  isNotEmpty(val): boolean {
    return val != null && val != '' && val != undefined;
  }

  isObjectDateModel(data: any): boolean {
    return (data instanceof DateModel || typeof data === 'object') && this.isNotEmptyDateModel(data);
  }

  parseDate(date: Date): DateModel {
    if (!date) { return null; }
    return {year : date.getFullYear(), month: date.getMonth() + 1, day: date.getDate()};
  }

  generateStateSSO() {
    let text = "";
    const possible = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  
    for (var i = 0; i < 10; i++)
      text += possible.charAt(Math.floor(Math.random() * possible.length));
  
    return text;
  }

}
